// PayFlow — server client.
//
// Talks to the payroll endpoints mounted at /payflow. Auth rides on cookies
// like the rest of the platform: every call sends `credentials: "include"` and
// the global fetch interceptor handles the 401 refresh, so no Authorization
// header is set here.
//
// Every non-OK response throws a PayflowApiError whose `message` is safe to
// drop straight into a banner. Nothing is retried — the caller decides.

import type {
  BreakRule,
  CommittedHolidays,
  ManualPoolState,
  StaffAccountsReport,
  SummaryOverride,
  TipRates,
  TipRatesLock,
} from "./payflowStore";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/payflow`;

/* ── Server payload types (the API contract) ──────────────────────────── */

// A payroll is assembled inside the request that asks for it, so by the time one exists
// it is finished — there is no in-flight stage for this client to see. The field is how
// the server marks a document complete, and a doc left mid-build by the asynchronous
// builder that used to write one is filtered out server-side and never arrives here.
export type PayrollStage = "ready";

// Real POS figures for one shift. Absent on rows with no sales record.
export interface PayrollSales {
  netSales: number;
  cash: number;
  ccTips: number;
  autoGrats: number;
}

export interface PayrollRow {
  // Employee id (Silverware username). "unlinked-<guid>" when the ETL couldn't
  // crosswalk the employee to a known profile.
  id: string;
  // Present (1, 2, …) only on an employee's 2nd+ shift of the same day.
  seq?: number;
  name: string;
  role: string;
  location?: string;
  // Minutes-from-midnight; may exceed 1440 on an overnight shift.
  //
  // A row is a match between a schedule and a worked shift, and either side can be
  // missing — an unscheduled shift, or a schedule nobody clocked in for. The missing
  // side's times are absent, never filled in from the side that is present.
  schedStart?: number;
  schedEnd?: number;
  actualIn?: number;
  actualOut?: number;
  sales?: PayrollSales;
}

export interface PayrollSummary {
  id: string; // == periodEnd, the period's natural key
  periodStart: string; // ISO date, 14 days inclusive ending periodEnd
  periodEnd: string;
  stage: PayrollStage;
  createdBy: string;
  createdAt: string; // ISO datetime
  // The rules this payroll was built under. Every figure on the payroll screens is
  // computed from these rather than from the live rules, so a rule edited today
  // cannot change what a finished payroll says.
  //
  // Undefined on payrolls built before rules were snapshotted — those fall back to
  // the live rules, and the screen says so rather than pretending otherwise.
  rulesSnapshot?: PayflowRules;
}

export interface PayrollDay {
  index: number; // 0..13
  dateISO: string;
  rows: PayrollRow[];
}

export interface PayrollDoc extends PayrollSummary {
  days: PayrollDay[];
  // Defects the build found in the source data it assembled — e.g. a sales record it
  // could not match to a worked shift, whose money may therefore be counted twice.
  // Carried on the payroll so the admin sees them next to the numbers they affect.
  warnings?: string[];
}

/* ── Source data changes ──────────────────────────────────────────────── */
// A payroll is a static snapshot: once built, its numbers never move. That is the
// point, and it means a payroll can quietly stop matching its source. This reports
// whether that has happened. Nothing acts on it — rebuilding is always the admin's
// explicit choice.

export type DriftStatus =
  | "clean" // nothing has changed that the server can see
  | "changed" // at least one day's source records differ
  | "unknown"; // no fingerprint to compare against (built before change tracking)

export type DriftReason = "hash" | "count" | "added" | "removed";

export interface DriftDay {
  dateISO: string;
  reason: DriftReason;
  storedCount: number;
  currentCount: number;
}

export interface DriftReport {
  status: DriftStatus;
  checkedAt: string;
  builtAt: string | null;
  days: DriftDay[];
  // An employee record this payroll references was edited. Never counted as drift:
  // the only thing a payroll takes from that record is a display name.
  employeesChanged: boolean;
  detail: string;
}

// Whether the source records behind a payroll have changed since it was built.
//
// Deliberately NOT "does this payroll match 7Shifts and Silverware" — while the
// pipeline suppresses deletes, a record removed at source leaves no trace for this to
// find. Wording that reaches a screen must stay within what this can actually prove.
export async function fetchPayrollDrift(id: string): Promise<DriftReport> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}/drift`, {
    credentials: "include",
  });
  if (!res.ok) throw await failureFor(res, "Could not check for source data changes");
  const body = await res.json();
  if (!body?.drift) {
    throw new PayflowApiError("The server did not return the change report.", res.status);
  }
  return body.drift as DriftReport;
}

// Flat pay figures that used to be hardcoded on the admin screen.
export interface PayConstants {
  baseHourlyRate: number;
  kitchenTipPoolPerHour: number;
  fohPremiumPerHour: number;
  vacationPayRate: number; // fraction, e.g. 0.04
}

// Drives the selectable period-ending dates on the admin screen.
export interface PayPeriodConfig {
  anchorEnd: string; // ISO date of a known period-ending day — always a SUNDAY
  lengthDays: number; // whole weeks; periods run Monday to Sunday
}

export interface PayflowRules {
  tipRates: TipRates;
  tipRatesLock: TipRatesLock | null;
  breakRules: BreakRule[];
  breakRulesLock: TipRatesLock | null;
  fohRoles: string[];
  fohRolesLock: TipRatesLock | null;
  salariedEmployeeIds: string[];
  salariedLock: TipRatesLock | null;
  committedHolidays: CommittedHolidays;
  payConstants: PayConstants;
  payPeriod: PayPeriodConfig;
}

export interface RulesResponse {
  rules: PayflowRules;
  etag: string | null; // null until the rules have been saved at least once
  updatedBy?: string;
  updatedAt?: string;
}

/* ── Errors ───────────────────────────────────────────────────────────── */

export class PayflowApiError extends Error {
  status: number;
  // The server's short error code ("exists", "busy", …) when it sent one.
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "PayflowApiError";
    this.status = status;
    if (code) this.code = code;
  }
}

// Short server codes expanded into something an admin can act on.
const ERROR_TEXT: Record<string, string> = {
  exists: "A payroll already exists for that period.",
  busy: "A payroll build is already running — wait for it to finish and try again.",
  // A build never pulls, so this is the one thing it cannot do for itself. Named as an
  // action rather than a fault: nothing went wrong and nothing was changed.
  "not-covered":
    "That pay period is not in PayFlow's data yet. Use 'Pull Data for this period' to fetch it — the payroll then builds on its own.",
};

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ISO yyyy-mm-dd → "Aug 9". Deliberately not payflowStore's fmtISODate: that module
// imports this one, and reaching back for a value would close the cycle at runtime.
const shortDate = (iso: string): string => {
  const [, month, day] = iso.split("-").map(Number);
  return month && day ? `${MONTH_ABBREVIATIONS[month - 1]} ${day}` : iso;
};

// The most days to name before the list stops being readable. Beyond this the count is
// the useful part — a whole fortnight missing is not a list of days to go and fetch, it
// is a period nothing has pulled.
const MAX_NAMED_DATES = 6;

// "Aug 8 and Aug 9", or "12 days" once naming them stops helping.
function describeDates(dates: string[]): string {
  if (dates.length > MAX_NAMED_DATES) return `${dates.length} days`;
  const named = dates.map(shortDate);
  if (named.length === 1) return named[0];
  return `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
}

// Build the error for a failed response, preferring the server's own wording
// (`message`, then `error`) over a generic status line.
async function failureFor(res: Response, fallback: string): Promise<PayflowApiError> {
  let code: string | undefined;
  let message = "";
  // Which days the stored data does not reach. Sent with a `not-covered` refusal, and
  // the difference between "pull that fortnight again" and "pull the two days that are
  // actually missing" — which is the whole of what the admin has to do next.
  let missingDates: string[] = [];
  try {
    const body = await res.json();
    if (typeof body?.error === "string") code = body.error;
    if (typeof body?.message === "string") message = body.message;
    if (Array.isArray(body?.missingDates)) {
      missingDates = body.missingDates.filter((d: unknown) => typeof d === "string");
    }
  } catch {
    // non-JSON error body — the status alone is the signal
  }
  if (code === "not-covered" && missingDates.length) {
    return new PayflowApiError(
      `PayFlow has no data for ${describeDates(missingDates)} in that pay period yet. ` +
        "Use 'Pull Data for this period' to fetch it — the payroll then builds on its own.",
      res.status,
      code,
    );
  }
  const text =
    message || (code && (ERROR_TEXT[code] || code)) || `${fallback} (${res.status})`;
  return new PayflowApiError(text, res.status, code);
}

/* ── Payrolls ─────────────────────────────────────────────────────────── */

// Every payroll, newest period first. Days are omitted — fetch them per payroll.
export async function listPayrolls(): Promise<PayrollSummary[]> {
  const res = await fetch(`${BASE_URL}/payrolls`, { credentials: "include" });
  if (!res.ok) throw await failureFor(res, "Could not load the payroll list");
  const body = await res.json();
  return Array.isArray(body?.payrolls) ? (body.payrolls as PayrollSummary[]) : [];
}

// One payroll with its 14 days of rows.
export async function fetchPayroll(id: string): Promise<PayrollDoc> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  if (!res.ok) throw await failureFor(res, "Could not load the payroll");
  const body = await res.json();
  if (!body?.payroll) {
    throw new PayflowApiError("The server did not return the payroll.", res.status);
  }
  return body.payroll as PayrollDoc;
}

// Build a payroll and return it. The server assembles it from data already stored and
// answers 201 with the finished document, rows and all — there is nothing to poll and
// no follow-up fetch, which is why this returns a PayrollDoc rather than a summary.
export async function createPayroll(
  periodEnd: string,
  replace: boolean,
): Promise<PayrollDoc> {
  const res = await fetch(`${BASE_URL}/payrolls`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(replace ? { periodEnd, replace: true } : { periodEnd }),
  });
  if (!res.ok) throw await failureFor(res, "Could not create the payroll");
  const body = await res.json();
  if (!body?.payroll) {
    throw new PayflowApiError("The server did not return the new payroll.", res.status);
  }
  return body.payroll as PayrollDoc;
}

// Delete a payroll. A 404 means it is already gone, which is the outcome the
// caller wanted, so it resolves rather than throwing.
export async function deletePayroll(id: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (res.ok || res.status === 404) return;
  throw await failureFor(res, "Could not delete the payroll");
}

/* ── Data pulls ───────────────────────────────────────────────────────── */
// Getting fresh figures out of 7Shifts and Silverware. Separate from building a
// payroll: a build reads what is already stored, so it is fast and never fails for a
// reason upstream, and this is the deliberate action that makes the stored data newer.

export type DataPullState =
  | "idle" // ReconState is readable and holds no run
  | "running"
  | "succeeded"
  | "partial"
  | "failed"
  // The server could not read ReconState at all. Distinct from "idle" on purpose: a
  // client that has just been handed a runId must not read "I cannot tell" as "your run
  // does not exist" and stop watching a pull that is still going.
  | "unknown"
  // The run was never marked finished and its lease has lapsed — the worker stopped
  // reporting. Terminal as far as a watcher is concerned: nothing will touch this doc
  // again except a brand new run, so there is nothing left to poll for.
  //
  // Before this existed such a run reported as "running" indefinitely, so every tab
  // that opened the screen started polling a pull that had ended hours earlier.
  | "stale";

export interface DataPullSource {
  source: string;
  name: string; // "7Shifts", "Silverware"
  state: "pending" | "pulling" | "ok" | "failed";
}

export interface DataCoverage {
  source: string;
  name: string;
  ok: boolean;
  lastPulledAt: string | null; // ISO datetime of the last successful pull
  coversFrom: string | null; // ISO dates of the window it covered
  coversTo: string | null;
}

// One step of the run, as the server ranked it. The server owns the order and the
// wording because it owns the phases; this screen only draws what it is given, which
// is what stops a pipeline that gains a phase from needing a UI release.
export type DataPullStageState = "done" | "active" | "failed" | "pending";

export interface DataPullStage {
  key: string;
  label: string;
  state: DataPullStageState;
}

export interface DataPullStatus {
  state: DataPullState;
  step: string; // what is happening, in words — always present
  // The run as an ordered list of steps. Empty when nothing has ever run.
  stages: DataPullStage[];
  detail?: string; // operator-facing; this screen is admin-only, so it is shown
  runId?: string;
  // Whether a person asked for this pull or the nightly schedule did. An admin told
  // "already running" needs to know which: one is worth waiting out, the other is
  // someone else's action.
  startedBy?: "admin" | "schedule";
  windowStart?: string;
  windowEnd?: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  sources: DataPullSource[];
  coverage: DataCoverage[];
}

// The window a pull should cover. Both dates are required together — the server
// rejects one without the other, because a half-specified window would silently
// fall back to the default and cover something other than what was asked for.
export interface DataPullWindow {
  startDate: string; // ISO yyyy-mm-dd, inclusive
  endDate: string; // ISO yyyy-mm-dd, inclusive
}

// Ask for a fresh pull. Resolves as soon as it has STARTED (202) — poll
// fetchDataPullStatus until `state` leaves "running". A 409 means one is already
// running, which is not an error worth retrying automatically.
//
// Omitting the window leaves the choice to the pipeline's default sliding window.
// The screen always supplies one; the parameter stays optional so a caller that has
// no opinion is not forced to invent dates.
export async function startDataPull(window?: DataPullWindow): Promise<{ runId: string }> {
  const res = await fetch(`${BASE_URL}/runs`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(window ?? {}),
  });
  if (!res.ok) throw await failureFor(res, "Could not start the data pull");
  const body = await res.json();
  return { runId: typeof body?.runId === "string" ? body.runId : "" };
}

// The current (or most recent) pull, plus how current the stored data is per source.
export async function fetchDataPullStatus(): Promise<DataPullStatus> {
  const res = await fetch(`${BASE_URL}/runs/current`, { credentials: "include" });
  if (!res.ok) throw await failureFor(res, "Could not read the data pull status");
  const body = await res.json();
  return {
    state: (body?.state as DataPullState) ?? "idle",
    step: typeof body?.step === "string" ? body.step : "",
    stages: Array.isArray(body?.stages) ? (body.stages as DataPullStage[]) : [],
    detail: typeof body?.detail === "string" ? body.detail : undefined,
    runId: typeof body?.runId === "string" ? body.runId : undefined,
    startedBy: body?.startedBy === "schedule" ? "schedule" : body?.startedBy === "admin" ? "admin" : undefined,
    windowStart: typeof body?.windowStart === "string" ? body.windowStart : undefined,
    windowEnd: typeof body?.windowEnd === "string" ? body.windowEnd : undefined,
    startedAt: body?.startedAt ?? null,
    finishedAt: body?.finishedAt ?? null,
    sources: Array.isArray(body?.sources) ? (body.sources as DataPullSource[]) : [],
    coverage: Array.isArray(body?.coverage) ? (body.coverage as DataCoverage[]) : [],
  };
}

// What is stored for one date. Three states because the two sources fail
// independently: a day whose sales never arrived is `partial`, and it is exactly the
// day worth re-pulling, so it must not look the same as a complete one.
export type DateCoverageState = "none" | "partial" | "full";

export interface DateCoverage {
  date: string; // ISO yyyy-mm-dd
  schedules: number; // 7Shifts scheduled shifts stored for the date
  shifts: number; // Silverware worked shifts stored for the date
  state: DateCoverageState;
}

// Which dates in a window already have data, one entry per date in the range asked
// for — including the empty ones, so a calendar can draw every cell without
// reconstructing the range to find the gaps.
export async function fetchDateCoverage(
  startISO: string,
  endISO: string,
): Promise<DateCoverage[]> {
  const query = new URLSearchParams({ start: startISO, end: endISO });
  const res = await fetch(`${BASE_URL}/runs/coverage?${query}`, {
    credentials: "include",
  });
  if (!res.ok) throw await failureFor(res, "Could not read which dates have data");
  const body = await res.json();
  return Array.isArray(body?.dates) ? (body.dates as DateCoverage[]) : [];
}

/* ── Decisions ────────────────────────────────────────────────────────── */
// Everything an admin or manager decides about a payroll. Stored server-side against
// the payroll, in its own document — the payroll itself is a static snapshot and never
// changes after it is built.
//
// Keys here carry no payroll id; the document is already scoped to one payroll. The
// store re-adds its `${periodId}-` prefix when it merges these into its own maps, which
// is why none of the screens' key shapes had to change.

export interface DecisionLock {
  by: string;
  at: string;
  reason?: string; // present when a stage was reopened
}

export interface PayrollDecisionState {
  adjustments: Record<string, { hours: number; reason: string; by: string; at: string }>;
  reviews: Record<
    string,
    {
      confirmedIn: number;
      confirmedOut: number;
      reason: string;
      confirmedBy: string;
      confirmedAt: string;
    }
  >;
  poolSolo: Record<string, string[]>; // dayIndex → employee ids removed from pooling
  manualPool: ManualPoolState | null;
  summaryOverrides: Record<string, SummaryOverride>; // `${empId}:${role}`
  staffAccounts: StaffAccountsReport | null;
  // `${dateISO}|${window}` → manager's allocation of server-to-bar money for a window with
  // no Main Bar bartender. `recipients` = employee ids to split it among (empty = nobody).
  barAllocations: Record<string, { recipients: string[]; by: string; at: string }>;
  lifecycle: {
    hours: DecisionLock | null;
    tips: DecisionLock | null;
    staffAccounts: DecisionLock | null;
    processed: DecisionLock | null;
  };
}

// The per-stage frozen outputs a payroll holds: what each verified tab showed when it was
// verified. Opaque here — each stage's shape is the frontend engine's, keyed by row or
// line. A stage absent here is not yet frozen (or predates freezing).
export interface FrozenStagesResponse {
  hours?: Record<string, unknown>;
  tips?: unknown;
  overview?: unknown;
}

export interface PayrollStateResponse {
  state: PayrollDecisionState;
  // The concurrency token. Null until the first decision is recorded; sent back on a
  // lock change so two admins racing one cannot both win.
  version: string | null;
  // The frozen stage outputs; a verified tab renders from these rather than recomputing.
  // Present on the state fetch; absent on an action response (which does not re-read them).
  frozen?: FrozenStagesResponse;
}

// One gesture. `clientActionId` is what makes a retry safe: the server writes the audit
// event under an id containing it, so a repeated request cannot apply twice.
export interface PayrollActionRequest {
  type: string;
  clientActionId: string;
  expectedVersion?: string | null;
  by: string; // display name for the audit log — never the identity of record
  subject?: string;
  reason?: string;
  [field: string]: unknown;
}

export async function fetchPayrollState(id: string): Promise<PayrollStateResponse> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}/state`, {
    credentials: "include",
  });
  if (!res.ok) throw await failureFor(res, "Could not load the payroll decisions");
  const body = await res.json();
  return {
    state: body.state as PayrollDecisionState,
    version: body.version ?? null,
    frozen: (body.frozen ?? {}) as FrozenStagesResponse,
  };
}

// Record a decision. The response carries the state as the server now holds it, so the
// caller replaces its copy rather than trusting the optimistic one it just applied.
export async function postPayrollAction(
  id: string,
  action: PayrollActionRequest,
): Promise<PayrollStateResponse> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}/actions`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(action),
  });
  if (!res.ok) throw await failureFor(res, "Could not record the change");
  const body = await res.json();
  return { state: body.state as PayrollDecisionState, version: body.version ?? null };
}

/* ── Processed register ───────────────────────────────────────────────── */
// What a payroll actually paid, as the Summary screen computed it, recorded when the
// payroll is processed. Stored verbatim: the server never recomputes these, which is
// what keeps a later change to the payroll engine from rewriting a finished payroll.

export interface RegisterLine {
  empId: string;
  name: string;
  role: string;
  netHours: number;
  rate: number;
  base: number;
  tipPool: number;
  foh: number;
  statPay: number;
  statWorked: boolean;
  tips: number;
  controlledTips: number;
  staffAccount: number;
  vacation: number;
}

export type RegisterTotals = Omit<RegisterLine, "empId" | "name" | "role" | "rate" | "statWorked">;

export interface PayrollRegister {
  lines: RegisterLine[];
  totals: RegisterTotals;
}

// A stored register, with the provenance the server stamped on it.
export interface StoredPayrollRegister extends PayrollRegister {
  id: string;
  periodEnd: string;
  sequence: number;
  computedAt: string;
  computedBy: string;
  inputs: {
    stateEtag: string;
    rulesSnapshotHash: string;
    sourceFingerprintHash: string;
  };
}

// Newest first. More than one means the payroll was processed, reopened and processed
// again — the earlier ones are kept on purpose.
export async function fetchPayrollRegisters(id: string): Promise<StoredPayrollRegister[]> {
  const res = await fetch(`${BASE_URL}/payrolls/${encodeURIComponent(id)}/registers`, {
    credentials: "include",
  });
  if (!res.ok) throw await failureFor(res, "Could not load the payroll registers");
  const body = await res.json();
  return Array.isArray(body?.registers) ? (body.registers as StoredPayrollRegister[]) : [];
}

/* ── Audit log ────────────────────────────────────────────────────────── */

export interface PayflowEvent {
  id: string;
  periodEnd: string;
  at: string;
  byUserID: string;
  byDisplay: string;
  ip: string;
  userAgent: string;
  actionType: string;
  action: string;
  subject: string;
  reason: string;
}

// Newest first, org-wide unless a payroll is named. Events outlive the payroll they
// describe — including decisions a rebuild discarded — so this is not scoped by default.
export async function fetchPayflowEvents(periodEnd?: string): Promise<PayflowEvent[]> {
  const path = periodEnd
    ? `${BASE_URL}/payrolls/${encodeURIComponent(periodEnd)}/events`
    : `${BASE_URL}/events`;
  const res = await fetch(path, { credentials: "include" });
  if (!res.ok) throw await failureFor(res, "Could not load the audit log");
  const body = await res.json();
  return Array.isArray(body?.events) ? (body.events as PayflowEvent[]) : [];
}

/* ── Payroll rules ────────────────────────────────────────────────────── */

export async function fetchRules(): Promise<RulesResponse> {
  const res = await fetch(`${BASE_URL}/rules`, { credentials: "include" });
  if (!res.ok) throw await failureFor(res, "Could not load the payroll rules");
  const body = await res.json();
  if (!body?.rules) {
    throw new PayflowApiError("The server did not return the payroll rules.", res.status);
  }
  return {
    rules: body.rules as PayflowRules,
    etag: typeof body.etag === "string" ? body.etag : null,
    updatedBy: typeof body.updatedBy === "string" ? body.updatedBy : undefined,
    updatedAt: typeof body.updatedAt === "string" ? body.updatedAt : undefined,
  };
}

// Save the rules. `etag` is the value from the last read; a 409 means someone
// else saved in the meantime and nothing was written.
// `note` describes which rule changed, in the words the screen used. The server keeps
// it as the subject of an audit entry — the rules document itself records only who
// saved it and when, which does not tell you what they did.
export async function saveRules(
  rules: PayflowRules,
  etag: string | null,
  note: string,
): Promise<{ rules: PayflowRules; etag: string | null }> {
  const res = await fetch(`${BASE_URL}/rules`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(etag ? { "If-Match": etag } : {}),
    },
    body: JSON.stringify({ rules, note }),
  });
  if (!res.ok) throw await failureFor(res, "Could not save the payroll rules");
  const body = await res.json();
  if (!body?.rules) {
    throw new PayflowApiError("The server did not return the saved rules.", res.status);
  }
  return {
    rules: body.rules as PayflowRules,
    etag: typeof body.etag === "string" ? body.etag : null,
  };
}
